import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

/* ═══════════════════════════════════════════════════════════
   DSR MATRIX SYNC — Standalone App
   Time, Attendance & Leave Management
   100% client-side — no backend required
   ═══════════════════════════════════════════════════════════ */

// Lazy load pages
const SyncDashboard = lazy(() => import('./pages/sync/SyncDashboard'));
const TimesheetPage = lazy(() => import('./pages/sync/TimesheetPage'));
const AttendancePage = lazy(() => import('./pages/sync/AttendancePage'));
const LeavesPage = lazy(() => import('./pages/sync/LeavesPage'));
const TeamPresence = lazy(() => import('./pages/sync/TeamPresence'));
const TimesheetApprovals = lazy(() => import('./pages/sync/TimesheetApprovals'));
const SyncReports = lazy(() => import('./pages/sync/SyncReports'));

// ── Theme Colors ──
const THEMES = {
  dark: {
    bgPrimary: '#0a0e1a', bgSecondary: '#0f1525', bgCard: '#131b2e',
    borderSubtle: 'rgba(255,255,255,0.06)', textPrimary: '#f0f2f5',
    textSecondary: '#a0aec0', textMuted: '#5a6b8a', accent: '#14b8a6',
    accentLight: '#2dd4bf', success: '#10b981', danger: '#ef4444', warning: '#f59e0b',
  },
  light: {
    bgPrimary: '#f5f7fa', bgSecondary: '#ffffff', bgCard: '#ffffff',
    borderSubtle: 'rgba(0,0,0,0.08)', textPrimary: '#1a202c',
    textSecondary: '#4a5568', textMuted: '#a0aec0', accent: '#14b8a6',
    accentLight: '#0d9488', success: '#10b981', danger: '#ef4444', warning: '#f59e0b',
  },
};

const NAV_ITEMS = [
  { section: 'Workspace' },
  { id: '/', icon: '📊', label: 'Dashboard' },
  { id: '/timesheet', icon: '⏱️', label: 'Timesheet' },
  { id: '/attendance', icon: '📅', label: 'Attendance' },
  { id: '/leaves', icon: '🌴', label: 'Time Off' },
  { section: 'Manager' },
  { id: '/team-presence', icon: '👥', label: 'Team Presence' },
  { id: '/sync-approvals', icon: '✅', label: 'Approvals' },
  { section: 'Analytics' },
  { id: '/sync-reports', icon: '📉', label: 'Reports' },
  { section: 'Info' },
  { id: '/about', icon: 'ℹ️', label: 'About & White Label' },
];

function PageLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 10, color: '#5a6b8a', fontSize: 13 }}>
      <div style={{ width: 20, height: 20, border: '2px solid #14b8a630', borderTop: '2px solid #14b8a6', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
      <span>Loading…</span>
    </div>
  );
}

// ── Login Page ──
function LoginPage({ onLogin, theme }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const t = THEMES[theme];

  return (
    <div style={{ fontFamily: "'Inter',-apple-system,sans-serif", minHeight: '100vh', background: t.bgPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 420, animation: 'fadeUp .5s ease' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 72, height: 72, borderRadius: 20, background: 'linear-gradient(135deg, #14b8a6, #2dd4bf)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 800, color: '#fff', margin: '0 auto 20px', boxShadow: '0 0 40px rgba(20,184,166,0.3)' }}>SY</div>
          <h1 style={{ fontSize: 32, fontWeight: 800, color: t.textPrimary, letterSpacing: '-.02em' }}>DSR <span style={{ color: '#14b8a6' }}>MATRIX</span> Sync</h1>
          <p style={{ color: t.textMuted, fontSize: 13, marginTop: 6 }}>Time, Attendance & Leave Management</p>
        </div>
        <form onSubmit={e => { e.preventDefault(); onLogin(); }} style={{ background: t.bgSecondary, borderRadius: 16, padding: '28px 24px', border: `1px solid ${t.borderSubtle}` }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: t.textSecondary, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>Email</label>
            <input type="text" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter any email" style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: `1px solid ${t.borderSubtle}`, background: t.bgCard, color: t.textPrimary, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ color: t.textSecondary, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter any password" style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: `1px solid ${t.borderSubtle}`, background: t.bgCard, color: t.textPrimary, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <button type="submit" style={{ width: '100%', padding: '14px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #14b8a6, #2dd4bf)', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 20px rgba(20,184,166,0.35)' }}>Enter Demo →</button>
          <div style={{ marginTop: 14, padding: '10px 12px', background: 'rgba(20,184,166,0.06)', border: '1px solid rgba(20,184,166,0.12)', borderRadius: 8, textAlign: 'center' }}>
            <span style={{ fontSize: 11, color: t.textMuted }}>No signup needed. Enter any credentials to explore. All data resets on refresh.</span>
          </div>
        </form>
        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 10, color: t.textMuted, opacity: 0.6 }}>
          <div>Powered by <strong style={{ color: '#14b8a6' }}>DSR MATRIX</strong> · <a href="https://dsrmatrix.com" target="_blank" rel="noreferrer" style={{ color: t.textMuted, textDecoration: 'none' }}>dsrmatrix.com</a></div>
          <div style={{ marginTop: 4 }}>White-label solution available · <a href="mailto:support@dsrmatrix.com" style={{ color: t.textMuted, textDecoration: 'none' }}>support@dsrmatrix.com</a></div>
        </div>
      </div>
    </div>
  );
}

// ── About & White Label Page ──
function AboutPage({ theme }) {
  const t = THEMES[theme];
  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 0' }}>
      <div style={{ background: t.bgCard, borderRadius: 16, padding: '32px 28px', border: `1px solid ${t.borderSubtle}`, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #14b8a6, #2dd4bf)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 18, fontWeight: 800 }}>DM</div>
          <div><h2 style={{ fontSize: 22, fontWeight: 800, color: t.textPrimary, margin: 0 }}>About DSR MATRIX</h2><p style={{ color: t.textMuted, fontSize: 12, margin: 0 }}>Enterprise Software Company</p></div>
        </div>
        <p style={{ color: t.textSecondary, fontSize: 14, lineHeight: 1.7, marginBottom: 16 }}>
          DSR MATRIX builds enterprise-grade software tools for workforce management, healthcare scheduling, manufacturing operations, and more. We specialize in creating customizable, white-label platforms that organizations can brand as their own.
        </p>
        <p style={{ color: t.textSecondary, fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>
          <strong style={{ color: t.textPrimary }}>DSR MATRIX Sync</strong> is our time, attendance, and leave management system. It provides precise time tracking, timesheets geared for project allocations, centralized leave management, and robust approvals workflows designed for modern organizations.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 24 }}>
          {[{ n: '45+', l: 'Demo Employees' }, { n: '10+', l: 'Recent Activity' }, { n: '4+', l: 'Timesheet Weeks' }, { n: '5+', l: 'Charge Codes' }].map((s, i) => (
            <div key={i} style={{ padding: '14px 12px', borderRadius: 10, background: 'rgba(20,184,166,0.06)', border: '1px solid rgba(20,184,166,0.12)', textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#14b8a6' }}>{s.n}</div>
              <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: 'linear-gradient(135deg, rgba(20,184,166,0.04), rgba(45,212,191,0.04))', borderRadius: 16, padding: '28px 24px', border: '1px solid rgba(20,184,166,0.15)', marginBottom: 20 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(20,184,166,0.1)', padding: '5px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: '#14b8a6', textTransform: 'uppercase', marginBottom: 14 }}>✨ White-Label Available</div>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: t.textPrimary, marginBottom: 8 }}>Deploy Sync as Your Own Product</h3>
        <p style={{ fontSize: 13, color: t.textSecondary, lineHeight: 1.6, marginBottom: 20 }}>Every tool on the DSR MATRIX marketplace can be white-labeled with your company branding, hosted on your own domain, with a dedicated database.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 20 }}>
          {[{ e: '🌐', l: 'Your Brand & Domain', d: 'Custom logo, colors, your URL' }, { e: '🗄️', l: 'Dedicated Database', d: 'Isolated, encrypted data' }, { e: '☁️', l: 'Cloud Hosted', d: 'Secure cloud infrastructure' }, { e: '🔒', l: 'Enterprise Security', d: 'SOC2-ready, role-based access' }].map((f, i) => (
            <div key={i} style={{ padding: 14, borderRadius: 10, background: t.bgCard, border: `1px solid ${t.borderSubtle}` }}>
              <div style={{ fontSize: 20, marginBottom: 6 }}>{f.e}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, marginBottom: 3 }}>{f.l}</div>
              <div style={{ fontSize: 10, color: t.textMuted }}>{f.d}</div>
            </div>
          ))}
        </div>
        <a href="mailto:support@dsrmatrix.com" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg, #14b8a6, #2dd4bf)', color: '#fff', padding: '12px 24px', borderRadius: 12, fontSize: 14, fontWeight: 600, textDecoration: 'none', boxShadow: '0 4px 20px rgba(20,184,166,0.3)' }}>✉️ Contact DSR MATRIX →</a>
      </div>

      <div style={{ background: t.bgCard, borderRadius: 16, padding: '24px 20px', border: `1px solid ${t.borderSubtle}` }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, marginBottom: 12 }}>Our Tools</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
          {[{ n: 'DSR MATRIX Nexus', d: 'Workforce Intelligence', c: '#6366f1', u: 'https://dsrmatrixnexus.online' },
            { n: 'DSR MATRIX Sync', d: 'Time & Attendance', c: '#14b8a6', u: 'https://dsrmatrixsync.online' },
            { n: 'DSR MATRIX CareForce', d: 'Healthcare Workforce', c: '#00ddb3', u: 'https://dsrmatrixcareforce.online' },
            { n: 'DSR MATRIX ERP', d: 'Manufacturing ERP', c: '#2563eb', u: 'https://dsrmatrixerp.online' },
          ].map((tool, i) => (
            <a key={i} href={tool.u} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 10, background: `${tool.c}08`, border: `1px solid ${tool.c}20`, textDecoration: 'none', transition: 'all 0.2s' }}>
              <div style={{ width: 8, height: 32, borderRadius: 4, background: tool.c }} />
              <div><div style={{ fontSize: 12, fontWeight: 600, color: tool.c }}>{tool.n}</div><div style={{ fontSize: 10, color: t.textMuted }}>{tool.d}</div></div>
            </a>
          ))}
        </div>
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <a href="https://dsrgrid.online" target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#14b8a6', textDecoration: 'none', fontWeight: 600 }}>Visit DSR MATRIX Marketplace → dsrgrid.online</a>
        </div>
      </div>
    </div>
  );
}

// ── App Layout ──
function AppLayout({ onLogout, theme, setTheme }) {
  const t = THEMES[theme];
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const [role, setRole] = useState('executive');

  // Fake user obj
  const user = { id: 1, name: 'Demo User', email: 'demo@company.com', role: role === 'executive' ? 'admin' : role, isDemo: true };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <div style={{
        width: sidebarCollapsed ? 60 : 240, minWidth: sidebarCollapsed ? 60 : 240,
        background: t.bgSecondary, borderRight: `1px solid ${t.borderSubtle}`,
        display: 'flex', flexDirection: 'column', transition: 'width 0.2s, min-width 0.2s',
        overflow: 'hidden', position: 'sticky', top: 0, height: '100vh',
      }}>
        {/* Logo */}
        <div style={{ padding: '16px 14px 8px', borderBottom: `2px solid #14b8a6`, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => setSidebarCollapsed(c => !c)}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, #14b8a6, #2dd4bf)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 900, flexShrink: 0 }}>SY</div>
          {!sidebarCollapsed && <span style={{ fontSize: 15, fontWeight: 800, background: 'linear-gradient(135deg, #14b8a6, #2dd4bf)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>DSR MATRIX Sync</span>}
        </div>

        {/* Demo banner */}
        {!sidebarCollapsed && (
          <div style={{ margin: '8px 12px 4px', padding: '6px 10px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.12)', borderRadius: 6, textAlign: 'center' }}>
            <span style={{ fontSize: 9, color: t.textMuted }}>⚡ Demo Mode — data resets on refresh</span>
          </div>
        )}

        {/* Role Switcher */}
        {!sidebarCollapsed && (
          <div style={{ margin: "4px 12px 10px", borderRadius: 8, background: t.bgCard, padding: 2, border: `1px solid ${t.borderSubtle}`, display: "flex" }}>
            {['staff', 'manager', 'executive'].map(r => (
              <button key={r} onClick={() => setRole(r)} style={{ flex: 1, padding: "6px 0", borderRadius: 6, border: "none", background: role === r ? t.accent : "transparent", color: role === r ? "#fff" : t.textSecondary, fontSize: 10, fontWeight: 600, cursor: "pointer", textTransform: "capitalize", transition: "all 0.2s", boxShadow: role === r ? "0 2px 4px rgba(0,0,0,0.1)" : "none" }}>{r}</button>
            ))}
          </div>
        )}

        {/* Nav */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '4px 8px' }}>
          {NAV_ITEMS.map((item, i) => {
            if (item.section) return !sidebarCollapsed ? (
              <div key={i} style={{ fontSize: 9, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.8px', padding: '12px 8px 4px', opacity: 0.6 }}>{item.section}</div>
            ) : <div key={i} style={{ height: 1, background: t.borderSubtle, margin: '6px 4px' }} />;
            const isActive = location.pathname === item.id;
            return (
              <button key={item.id} onClick={() => navigate(item.id)} style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                padding: sidebarCollapsed ? '10px 0' : '8px 12px', borderRadius: 8,
                border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: isActive ? 600 : 400,
                background: isActive ? 'rgba(20,184,166,0.12)' : 'transparent',
                color: isActive ? '#2dd4bf' : t.textSecondary,
                transition: 'all 0.15s', justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                fontFamily: 'inherit',
              }} title={item.label}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
                {!sidebarCollapsed && item.label}
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div style={{ padding: '12px 14px', borderTop: `1px solid ${t.borderSubtle}` }}>
          {!sidebarCollapsed && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(20,184,166,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#14b8a6' }}>DU</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 500, color: t.textPrimary }}>Demo User</div>
                  <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'rgba(20,184,166,0.15)', color: '#14b8a6', fontWeight: 600 }}>DEMO</span>
                </div>
                {/* Theme toggle */}
                <button onClick={() => setTheme(th => th === 'dark' ? 'light' : 'dark')} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${t.borderSubtle}`, background: 'transparent', color: t.textSecondary, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Toggle theme">
                  {theme === 'dark' ? '☀️' : '🌙'}
                </button>
                {/* Logout */}
                <button onClick={onLogout} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${t.borderSubtle}`, background: 'transparent', color: t.textSecondary, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Sign out">🚪</button>
              </div>
              <div style={{ fontSize: 9, color: t.textMuted, opacity: 0.5 }}>
                Powered by <strong>DSR MATRIX</strong><br />
                <a href="mailto:support@dsrmatrix.com" style={{ color: 'inherit', textDecoration: 'none' }}>support@dsrmatrix.com</a> · <a href="https://dsrmatrix.com" target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>dsrmatrix.com</a>
              </div>
            </>
          )}
          {sidebarCollapsed && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <button onClick={() => setTheme(th => th === 'dark' ? 'light' : 'dark')} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${t.borderSubtle}`, background: 'transparent', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{theme === 'dark' ? '☀️' : '🌙'}</button>
              <button onClick={onLogout} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${t.borderSubtle}`, background: 'transparent', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🚪</button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
        {/* Demo banner */}
        <div style={{ background: 'rgba(245,158,11,0.06)', borderBottom: '1px solid rgba(245,158,11,0.12)', padding: '6px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 11, color: t.textMuted }}>
          <span>⚡</span>
          <span>Demo Mode — explore freely! Changes are <strong>temporary</strong> and reset on refresh. <a href="mailto:support@dsrmatrix.com" style={{ color: '#14b8a6', textDecoration: 'none', fontWeight: 500 }}>Contact us</a> for your workspace.</span>
        </div>
        <div style={{ padding: 20, animation: 'fadeUp 0.3s ease' }}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<SyncDashboard user={user} />} />
              <Route path="/timesheet" element={<TimesheetPage user={user} />} />
              <Route path="/attendance" element={<AttendancePage user={user} />} />
              <Route path="/leaves" element={<LeavesPage user={user} />} />
              <Route path="/team-presence" element={<TeamPresence user={user} />} />
              <Route path="/sync-approvals" element={<TimesheetApprovals user={user} />} />
              <Route path="/sync-reports" element={<SyncReports user={user} />} />
              <Route path="/about" element={<AboutPage theme={theme} />} />
              <Route path="*" element={<SyncDashboard user={user} />} />
            </Routes>
          </Suspense>
        </div>
      </div>
    </div>
  );
}

// ── Root Component ──
export default function App() {
  const [loggedIn, setLoggedIn] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem('sync_theme') || 'dark');
  const t = THEMES[theme];

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.style.setProperty('--accent', t.accent);
    document.documentElement.style.setProperty('--accent-light', t.accentLight);
    document.documentElement.style.setProperty('--accent-dark', t.accent); // for sync, keep it simple
    localStorage.setItem('sync_theme', theme);
  }, [theme, t]);

  return (
    <BrowserRouter>
      <Toaster position="top-right" toastOptions={{ style: { background: '#1e1e24', color: '#f4f4f5', border: '1px solid rgba(255,255,255,0.1)', fontSize: '13px' }, duration: 3000 }} />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Inter', -apple-system, sans-serif; background: ${t.bgPrimary}; color: ${t.textPrimary}; }
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px) } to { opacity: 1; transform: translateY(0) } }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${t.borderSubtle}; border-radius: 3px; }
        
        /* Base Variables overrides to match theme */
        :root {
          --bg-primary: ${t.bgPrimary};
          --bg-secondary: ${t.bgSecondary};
          --bg-card: ${t.bgCard};
          --border-subtle: ${t.borderSubtle};
          --text-primary: ${t.textPrimary};
          --text-secondary: ${t.textSecondary};
          --text-muted: ${t.textMuted};
          
          --accent: ${t.accent};
          --accent-light: ${t.accentLight};
          --accent-dark: ${t.accent};
          
          --success: ${t.success};
          --danger: ${t.danger};
          --warning: ${t.warning};
        }
      `}</style>
      
      {!loggedIn ? (
        <LoginPage onLogin={() => setLoggedIn(true)} theme={theme} />
      ) : (
        <AppLayout onLogout={() => setLoggedIn(false)} theme={theme} setTheme={setTheme} />
      )}
    </BrowserRouter>
  );
}
